<script lang="ts">
    import type {LocalizeFunction} from '../../svelte/types';
    import {getContext, onMount} from 'svelte';
    import {CONTEXT_PATH_KEY, DATE_FORMATTER_KEY, INJECTOR_KEY, LOCALIZE_KEY, pipe} from '../../svelte/utils';
    import type {RecordCollectionManager} from '../../record-collections/record-collection-manager';
    import type {RecordCollectionViewConfiguration} from '../../typings/portaro.fe.types';
    import KpRecordCollection from '../../record-collections/kp-record-collection/KpRecordCollection.svelte';
    import KpVisitedPanel from '../../commons/components/kp-visited-panel/KpVisitedPanel.svelte';
    import KpGenericHierarchyTreesView
        from '../../commons/components/kp-generic-hierarchy-trees-view/KpGenericHierarchyTreesView.svelte';
    import type {DateFormatter, Injector} from '../../svelte/types';
    import type {
        HierarchyTreeNode,
        RecordCollectionDetail,
        RecordCollectionHierarchyTreeNode,
        UUID
    } from '../../typings/portaro.be.types';
    import {fade} from 'svelte/transition';
    import KpLoadingInline from '../../commons/components/kp-loading/KpLoadingInline.svelte';
    import KpPluralizeText from '../../commons/components/kp-pluralize-text/KpPluralizeText.svelte';
    import {KpRecordCollectionContentPagePresenter} from './kp-record-collection-content-page.presenter';

    export let recordCollectionManager: RecordCollectionManager;
    export let viewConfiguration: RecordCollectionViewConfiguration;

    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);
    const dateFormatter: DateFormatter = getContext(DATE_FORMATTER_KEY);
    const CONTEXT_PATH: string = getContext(CONTEXT_PATH_KEY);
    const injector: Injector = getContext(INJECTOR_KEY);
    const presenter: KpRecordCollectionContentPagePresenter = injector.getByToken(KpRecordCollectionContentPagePresenter.presenterName);

    const recordCollection = recordCollectionManager.getRecordCollection();

    let collectionSize = recordCollectionManager.getSize();
    let subcollectionsSize = recordCollectionManager.getSubCollectionsSize();
    let recordCollectionDetail: RecordCollectionDetail;

    onMount(async () => {
        await loadCollectionDetail()
    });

    async function loadCollectionDetail() {
        recordCollectionDetail = await presenter.getRecordCollectionDetail(recordCollection);
    }

    async function updateCollectionsInformation() {
        collectionSize = recordCollectionManager.getSize();
        subcollectionsSize = recordCollectionManager.getSubCollectionsSize();
        await loadCollectionDetail()
    }

    function recastToRecordCollectionTreeNode(node: HierarchyTreeNode<UUID | number | string>): RecordCollectionHierarchyTreeNode {
        return node as RecordCollectionHierarchyTreeNode;
    }
</script>

<template>
    <div class="row">
        <h1 class="col-sm-12">{recordCollectionManager.getRecordCollection().name}</h1>
    </div>

    <div class="row">
        <div class="col-sm-8 main-panel-hlavni">
            <div class="back-to-collections-link">
                <a href="{CONTEXT_PATH}/#!/record-collections">
                    {localize(/* @kp-localization recordCollections.BackToCollections */ 'recordCollections.BackToCollections')}
                </a>
            </div>

            {#await presenter.getRecordCollectionHierarchyTree(recordCollection) then hierarchyTree}
                <div class="vystouplyDiv record-collection-hierarchy-panel" transition:fade={{duration:200}}>
                    <KpGenericHierarchyTreesView hierarchyTrees={[hierarchyTree]} let:node>
                        <!-- if the next line is red then IDEA/Svelte plugin still do not support new features (ignore it) -->
                        {@const collectionTreeNode = recastToRecordCollectionTreeNode(node)}
                        {#if collectionTreeNode.current}
                            <strong>{collectionTreeNode.text}</strong>
                        {:else }
                            <a href="{CONTEXT_PATH}/#!/record-collections/{collectionTreeNode.id}">{collectionTreeNode.text}</a>
                        {/if}
                    </KpGenericHierarchyTreesView>
                </div>
            {/await}

            {#if !!recordCollection.note}
                <p class="record-collection-note">{recordCollection.note}</p>
            {/if}

            <div class="record-collection-detail">
                <span class="creation-date">
                    {localize(/* @kp-localization commons.DatumVytvoreni */ 'commons.DatumVytvoreni')}:
                    {#if !recordCollectionDetail}
                        <KpLoadingInline size="xs"/>
                    {:else}
                        {pipe(recordCollectionDetail.creationEvent.createDate, dateFormatter('d. M. yyyy'))}
                    {/if}
                </span>
                <span class="last-update-date">
                    {localize(/* @kp-localization commons.LastUpdateDate */ 'commons.LastUpdateDate')}:
                    {#if !recordCollectionDetail}
                        <KpLoadingInline size="xs"/>
                    {:else}
                        {pipe(recordCollectionDetail.lastUpdateEvent.createDate, dateFormatter('d. M. yyyy'))}
                    {/if}
                </span>
            </div>

            <div class="record-collection-size">
                <span class="collection-size-counter">
                    <KpPluralizeText
                            count={collectionSize}
                            zeroCountText={localize(/* @kp-localization recordCollections.zadneZaznamy */ 'recordCollections.zadneZaznamy')}
                            oneCountText={localize(/* @kp-localization recordCollections.1Zaznam */ 'recordCollections.1Zaznam')}
                            twoThreeFourCountText={localize(/* @kp-localization recordCollections.xZaznamy */ 'recordCollections.xZaznamy')}
                            moreText={localize(/* @kp-localization recordCollections.xZaznamu */ 'recordCollections.xZaznamu')}/>
                    {localize(/* @kp-localization recordCollections.inCollection */ 'recordCollections.inCollection')}
                </span>
                <span class="sub-collections-size-counter">
                    <KpPluralizeText
                            count={subcollectionsSize}
                            zeroCountText={localize(/* @kp-localization recordCollections.zadneZaznamy */ 'recordCollections.zadneZaznamy')}
                            oneCountText={localize(/* @kp-localization recordCollections.1Zaznam */ 'recordCollections.1Zaznam')}
                            twoThreeFourCountText={localize(/* @kp-localization recordCollections.xZaznamy */ 'recordCollections.xZaznamy')}
                            moreText={localize(/* @kp-localization recordCollections.xZaznamu */ 'recordCollections.xZaznamu')}/>
                    {localize(/* @kp-localization recordCollections.inSubcollections */ 'recordCollections.inSubcollections')}
                </span>
            </div>

            <KpRecordCollection {recordCollectionManager} {viewConfiguration} on:update={updateCollectionsInformation}/>
        </div>

        <!-- pravy sloupec -->
        <aside class="col-sm-4 main-panel-pravy">
            <KpVisitedPanel/>
        </aside>
    </div>
</template>

<style lang="less">
  .record-collection-hierarchy-panel {
    margin: 10px 0;
    padding: 10px;
  }

  .record-collection-detail, .record-collection-size {
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
  }
</style>